/******* Angular Resourse *******/
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

/******* Shared Resourse *******/
import { UtilsService } from './utils.service';
import { PrivilegeService } from './privilege.service';
import { GlobalUIBlockingService } from './global-ui-blocking.service';
import { FileType } from '@core/types';

/******* Plug-In Resourse *******/
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';

/******* API Resourse *******/
import { Files } from '@api';

@Injectable({
    providedIn: 'root',
})
export class FileService {

    // Public
    public currentFirmwareList: Observable<any>;
    public currentConfigurationList: Observable<any>;
    public currentNewFirmwareList: Observable<any>;
    public currentModuleFirmwareList: Observable<any>;

    // Private
    private currentFirmwareListSubject: BehaviorSubject<any>;
    private currentConfigurationListSubject: BehaviorSubject<any>;
    private currentNewFirmwareListSubject: BehaviorSubject<any>;
    private currentModuleFirmwareListSubject: BehaviorSubject<any>;

    /**
     * Constructor
     * 
     * @param {Router} _router
     * @param {Files} _files
     * @param {UtilsService} _utilsService
     * @param {PrivilegeService} _privilegeService
     * @param {GlobalUIBlockingService} _globalUIBlockingService
     */
    constructor(private _router: Router,
        private _files: Files,
        private _utilsService: UtilsService,
        private _privilegeService: PrivilegeService,
        private _globalUIBlockingService: GlobalUIBlockingService) {

        // Firmware initial
        this.currentFirmwareListSubject = new BehaviorSubject<Array<any>>(null);
        this.currentFirmwareList = this.currentFirmwareListSubject.asObservable()
            .pipe(tap((x) => { if (!x) this._queryFiles(FileType.FIRMWARE); }));

        // Configuration initial
        this.currentConfigurationListSubject = new BehaviorSubject<Array<any>>(null);
        this.currentConfigurationList = this.currentConfigurationListSubject.asObservable()
            .pipe(tap((x) => { if (!x) this._queryFiles(FileType.CONFIGURATION); }));

        // New Firmware initial
        this.currentNewFirmwareListSubject = new BehaviorSubject<Array<any>>(null);
        this.currentNewFirmwareList = this.currentNewFirmwareListSubject.asObservable()
            .pipe(tap((x) => {
                if (!x && this._router.url.startsWith('/pages') && this._router.url != '/pages/files-management') this._queryNewFirmware();
            }));

        // Module Firmware initial
        this.currentModuleFirmwareListSubject = new BehaviorSubject<Array<any>>(null);
        this.currentModuleFirmwareList = this.currentModuleFirmwareListSubject.asObservable()
            .pipe(tap((x) => { if (!x) this._queryFiles(FileType.MODULE_FIRMWARE); }));

    }

    //  Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get currrent firmware list Value
     */
    get currentFirmwareListValue() {
        return this.currentFirmwareListSubject.value;
    }

    /**
     * Get currrent configuration list Value
     */
    get currentLogsValue() {
        return this.currentConfigurationListSubject.value;
    }

    // Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Query Device Status Data
     * 
     * @param {FileType} fileType
     * @param resolve
     * @param rejects
     */
    private _queryFiles(fileType: FileType, resolve = null, rejects = null): void {
        this._privilegeService.currentUser.pipe(filter(x => x != null), take(1)).subscribe((userData) => {
            const owner = userData.id;
            const fileTypeCondition = fileType === FileType.MODULE_FIRMWARE ? {
                fileType: { inq: [FileType.DFOTA_IMAGE] }
            } : { fileType };
            let conditionArray: Array<any> = [
                { ...fileTypeCondition },
                { userId: owner } // shared file
            ];
            this._globalUIBlockingService.start();
            this._files.getFiles({
                order: "updatedAt DESC",
                limit: 1000,
                where: {
                    and: [...conditionArray]
                }
            }).subscribe((returnData) => {
                this._globalUIBlockingService.stop();
                const fileListData = returnData.data;
                switch (fileType) {
                    case FileType.FIRMWARE:
                        this.currentFirmwareListSubject.next(fileListData);
                        this.currentNewFirmwareListSubject.next(fileListData.filter(x => x.ackReceived === false))
                        break;
                    case FileType.CONFIGURATION:
                        this.currentConfigurationListSubject.next(fileListData);
                        break;
                    case FileType.MODULE_FIRMWARE:
                        this.currentModuleFirmwareListSubject.next(fileListData);
                        break;
                }
                if (resolve) resolve(fileListData);
            }, (error) => {
                this._globalUIBlockingService.stop();
                this._utilsService.alert('error', `Fail to load ${fileType}`, error);
                if (rejects) rejects(false);
            });
        });
    }

    /**
     * Query Device Status Data
     * 
     * @param {FileType} fileType
     * @param resolve
     * @param rejects
     */
    private _queryNewFirmware(resolve = null, rejects = null): void {
        this._privilegeService.currentUser.pipe(filter(x => x != null), take(1)).subscribe((userData) => {
            const owner = userData.id;
            let conditionArray: Array<any> = [
                { fileType: FileType.FIRMWARE },
                { ackReceived: false },
                { userId: owner } // shared file
            ];
            this._globalUIBlockingService.start();
            this._files.getFiles({
                order: "updatedAt DESC",
                limit: 1000,
                where: {
                    and: [...conditionArray]
                }
            }).subscribe((returnData) => {
                this._globalUIBlockingService.stop();
                const fileListData = returnData.data;
                this.currentNewFirmwareListSubject.next(fileListData);
                if (resolve) resolve(fileListData);
            }, (error) => {
                this._globalUIBlockingService.stop();
                this._utilsService.alert('error', `Fail to load ${FileType.FIRMWARE}`, error);
                if (rejects) rejects(false);
            });
        });
    }

    // Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Refresh files by type
     * 
     * @param {FileType} fileType
     * @returns {Promise<any>}
     */
    public refreshFiles(fileType: FileType): Promise<any> {
        return new Promise((resolve, rejects) => this._queryFiles(fileType, resolve, rejects));
    }

    /**
     * Refresh release files by type
     * 
     * @returns {Promise<any>}
     */
    public refreshReleaseFiles(): Promise<any> {
        return new Promise((resolve, rejects) => this._queryNewFirmware(resolve, rejects));
    }

    /**
     * Clear the files by type
     * 
     * @param {FileType} fileType
     */
    public clearAllInfo(fileType: FileType): void {
        switch (fileType) {
            case FileType.FIRMWARE:
                this.currentFirmwareListSubject.next(null);
                this.currentNewFirmwareListSubject.next(null);
                break;
            case FileType.CONFIGURATION:
                this.currentConfigurationListSubject.next(null);
                break;
            case FileType.MODULE_FIRMWARE:
                this.currentModuleFirmwareListSubject.next(null);
                break;
        };
    }
}